import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Download, Users, Calendar, DollarSign, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SubscriberManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plan: 'monthly',
    amount: 99,
    upi_transaction_id: '',
  });

  const plans = [
    { value: 'monthly', label: 'Monthly', amount: 99 },
    { value: 'quarterly', label: 'Quarterly (3 months)', amount: 249 },
    { value: 'yearly', label: 'Yearly (12 months)', amount: 899 },
  ];

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/subscribers`, getAuthHeaders());
      setSubscribers(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch subscribers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/admin/subscribers`, formData, getAuthHeaders());
      toast({ title: 'Success', description: 'Subscriber added successfully' });
      resetForm();
      fetchSubscribers();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to add subscriber', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      plan: 'monthly',
      amount: 99,
      upi_transaction_id: '',
    });
  };

  const handlePlanChange = (plan) => {
    const selected = plans.find(p => p.value === plan);
    setFormData({ ...formData, plan, amount: selected.amount });
  };

  const exportSubscribers = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Plan', 'Amount', 'Status', 'Start Date', 'End Date'],
      ...filteredSubscribers.map(s => [
        s.name,
        s.email,
        s.phone,
        s.plan,
        s.amount,
        s.status,
        new Date(s.start_date).toLocaleDateString(),
        new Date(s.end_date).toLocaleDateString(),
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'subscribers.csv';
    a.click();
  };

  const filteredSubscribers = filter === 'all' 
    ? subscribers 
    : subscribers.filter(s => s.status === filter);

  const stats = {
    total: subscribers.length,
    active: subscribers.filter(s => s.status === 'active').length,
    expired: subscribers.filter(s => s.status === 'expired').length,
    revenue: subscribers.reduce((sum, s) => sum + (s.amount || 0), 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Subscriber Management</h1>
          <p className="text-gray-600 mt-1">Manage e-paper subscriptions</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportSubscribers} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Subscriber
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Subscribers</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-10 h-10 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-3xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Expired</p>
                <p className="text-3xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-3xl font-bold">₹{stats.revenue}</p>
              </div>
              <DollarSign className="w-10 h-10 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-red-600 hover:bg-red-700' : ''}
        >
          All ({subscribers.length})
        </Button>
        <Button
          variant={filter === 'active' ? 'default' : 'outline'}
          onClick={() => setFilter('active')}
          className={filter === 'active' ? 'bg-green-600 hover:bg-green-700' : ''}
        >
          Active ({stats.active})
        </Button>
        <Button
          variant={filter === 'expired' ? 'default' : 'outline'}
          onClick={() => setFilter('expired')}
          className={filter === 'expired' ? 'bg-gray-600 hover:bg-gray-700' : ''}
        >
          Expired ({stats.expired})
        </Button>
      </div>

      {/* Subscribers List */}
      <div className="space-y-3">
        {loading && subscribers.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No subscribers found</p>
            </CardContent>
          </Card>
        ) : (
          filteredSubscribers.map((subscriber) => (
            <Card key={subscriber.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1 grid grid-cols-5 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-semibold">{subscriber.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="text-sm">{subscriber.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="text-sm">{subscriber.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Plan</p>
                      <Badge>{subscriber.plan}</Badge>
                      <p className="text-sm font-semibold mt-1">₹{subscriber.amount}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <Badge variant={subscriber.status === 'active' ? 'default' : 'secondary'}
                        className={subscriber.status === 'active' ? 'bg-green-600' : 'bg-gray-500'}>
                        {subscriber.status}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-1">
                        Expires: {new Date(subscriber.end_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Subscriber Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Subscriber</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
            </div>

            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>Subscription Plan *</Label>
              <select
                value={formData.plan}
                onChange={(e) => handlePlanChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {plans.map((plan) => (
                  <option key={plan.value} value={plan.value}>
                    {plan.label} - ₹{plan.amount}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                required
              />
            </div>

            <div>
              <Label>UPI Transaction ID (Optional)</Label>
              <Input
                value={formData.upi_transaction_id}
                onChange={(e) => setFormData({ ...formData, upi_transaction_id: e.target.value })}
                placeholder="e.g., 123456789012"
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Adding...' : 'Add Subscriber'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriberManagement;